import Moment from 'moment';
import {
  ONE_MINUTE,
  TWENTY_FOUR_HOURS,
  SIXTY_MINUTES,
  FOUR_WEEKS,
  SEVEN_DAYS,
  ONE_MONTH,
  editZoneOccurrenceOptions,
} from '../components/RelativeDateAndTime/constants/index';

/**
 *
 * Creates an API friendly date format from the Relative Date and Time Widget.
 *
 * @param { Object } rdatDate - Date object from api
 * @param { Boolean } isTemplateRdat - if rdat was pulled from a template
 * @returns { Object } Object compatible with RelativeDateAndTime Component
 * The date object received from the api needs to be converted into an RDAT Object compatible with our UX
 */
export const transformDateFieldToRDAT = (
  rdatDate,
  isTemplateRdat = false,
  onPrices
) => {
  // For date object without a relative date specified, just return a simple RDAT field.
  if (!rdatDate || !rdatDate.relativeDate) {
    return {
      date: isTemplateRdat ? '' : rdatDate.dateTime,
      fieldId: rdatDate.fieldId,
    };
  }

  const transformedDateField = {};
  const diffInSeconds = rdatDate.relativeDate.value;
  const isAbsoluteTime = rdatDate.relativeDate.isAbsoluteTime;
  const operator = calculateOperator(
    rdatDate.relativeDate.operator,
    diffInSeconds,
    isAbsoluteTime
  );
  const mathOperation = calculateMathOperation(rdatDate.relativeDate.operator);
  const when = calculateBeforeOrAfter(
    mathOperation,
    diffInSeconds,
    isAbsoluteTime,
    rdatDate.relativeDate.anchorFieldId
  );
  const anchorDateValue = Moment(rdatDate.dateTime)[mathOperation](
    diffInSeconds,
    'seconds'
  );
  const rdatMomentValue = Moment(rdatDate.dateTime);
  const { months, weeks, hours, days, minutes } = getTimeUnitSelections(
    rdatMomentValue,
    anchorDateValue,
    diffInSeconds
  );
  const occurrence = calculateOccurrence(
    when,
    rdatDate.relativeDate.anchorFieldId
  )[0];
  const specificTime = '';
  const meridiem = '';
  if (onPrices) {
    transformedDateField.dateTime = rdatDate.dateTime;
  }
  Object.assign(
    transformedDateField,
    { date: isTemplateRdat ? '' : rdatDate.dateTime },
    { operator },
    { occurrence },
    { specificTime },
    { meridiem },
    { months },
    { weeks },
    { days },
    { hours },
    { minutes },
    { anchor: rdatDate.relativeDate.anchorFieldId },
    { fieldId: rdatDate.fieldId },
    { isRelative: true },
    { id: isTemplateRdat ? '' : rdatDate.relativeDate.id }
  );

  if (rdatDate.relativeDate.isAbsoluteTime) {
    const meridiem = rdatMomentValue
      .localeData()
      .meridiem(rdatMomentValue.hour())
      .toLowerCase();
    const specificTime = rdatMomentValue.format('H:mm');
    Object.assign(transformedDateField, { specificTime, meridiem });
  }
  return transformedDateField;
};

/**
 *
 * @param { String } when - determines before or after anchor
 * @param  { Number } anchorFieldId - id of the anchor field the rdat is subscribed to
 * @returns { Array } the occurrence option the user originally selected
 */
const calculateOccurrence = (when, anchorFieldId) => {
  return editZoneOccurrenceOptions.filter((option) => {
    const firstWord = option.name.split(' ')[0].toLowerCase();
    return (
      Number(option.anchor) === Number(anchorFieldId) &&
      firstWord === when.toLowerCase()
    );
  });
};

/**
 *
 * @param { String } rdatOperator - operator on RDAT object from API
 * @param { Number } diffInSeconds - difference in seconds from anchor date, from RDAT object from API
 * @param { Boolean } isAbsoluteTime - whether the user has set a specific time
 * @param { Number } anchorFieldId - anchor fieldId for occurence option
 * @return { String } when - Before, After, or On anchor date
 * @param { Number } anchorFieldId - anchor fieldId for occurence option
 */
const calculateBeforeOrAfter = (
  rdatOperator,
  diffInSeconds,
  isAbsoluteTime,
  anchorFieldId
) => {
  // no specific time, less than 24 hours, can still be before or after
  const secondsInADay = 86400;
  let when;
  if (anchorFieldId === 2) return 'After';
  if (diffInSeconds === 0) return 'On';
  // if user selects ON anchor, the value or diff in seconds will always be zero
  if (
    diffInSeconds > secondsInADay - 1 ||
    (diffInSeconds < secondsInADay - 1 && !isAbsoluteTime) ||
    (diffInSeconds < secondsInADay - 1 && isAbsoluteTime)
  ) {
    // we know user has NOT selected 'on' anchor date
    when = rdatOperator === 'add' ? 'After' : 'Before';
  } else {
    when = 'On';
  }
  return when;
};

/**
 * @param { String } operator - either '>' or '<'
 * @return { String } either add or subtract
 */

const calculateMathOperation = (operator) => {
  return operator === '>' ? 'add' : 'subtract';
};

/**
 *
 * @param { String } rdatOperator - operator on RDAT object from API
 * @param { Number } diffInSeconds - difference in seconds from anchor date, from RDAT object from API
 * @param { Boolean } isAbsoluteTime - whether the user has set a specific time
 * @return { String / Null } subtract, add, or null ( null means ON is when )
 */
export const calculateOperator = (
  rdatOperator,
  diffInSeconds,
  isAbsoluteTime
) => {
  const secondsInADay = 86400;
  if (diffInSeconds >= 0 && diffInSeconds <= secondsInADay - 1) {
    if (!isAbsoluteTime) {
      return rdatOperator === '>' ? 'add' : 'subtract';
    }
    return null;
  }
  return rdatOperator === '>' ? 'add' : 'subtract';
};

/**
 *
 * @param { Moment / Object } rdatDate - rdat date as a  moment object
 * @param { Moment / Object } anchorDate - anchor date as a moment object
 * @param { Number } diffInSeconds - difference in seconds value returned from api
 * @return { Object } highestTimeUnit - highest Integer timeUnit and timeUnitAmount
 */
const getTimeUnitSelections = (rdatDate, anchorDate, diffInSeconds) => {
  if (diffInSeconds < SIXTY_MINUTES) {
    // Minutes.
    const minutes = Math.round(diffInSeconds / ONE_MINUTE);
    return { months: 0, weeks: 0, days: 0, hours: 0, minutes };
  } else if (diffInSeconds < TWENTY_FOUR_HOURS) {
    // Hours
    const hours = Math.floor(diffInSeconds / SIXTY_MINUTES);
    const minutes = Math.round(
      (diffInSeconds - hours * SIXTY_MINUTES) / ONE_MINUTE
    );
    return { months: 0, weeks: 0, days: 0, hours, minutes };
  } else if (diffInSeconds < SEVEN_DAYS) {
    // Days
    const days = Math.floor(diffInSeconds / TWENTY_FOUR_HOURS);
    const hours = Math.floor(
      (diffInSeconds - days * TWENTY_FOUR_HOURS) / SIXTY_MINUTES
    );
    const minutes = Math.round(
      (diffInSeconds - days * TWENTY_FOUR_HOURS - hours * SIXTY_MINUTES) /
        ONE_MINUTE
    );
    return { months: 0, weeks: 0, days, hours, minutes };
  } else if (diffInSeconds < FOUR_WEEKS) {
    // Weeks
    const weeks = Math.floor(diffInSeconds / SEVEN_DAYS);
    const days = Math.floor(
      (diffInSeconds - weeks * SEVEN_DAYS) / TWENTY_FOUR_HOURS
    );
    const hours = Math.floor(
      (diffInSeconds - weeks * SEVEN_DAYS - days * TWENTY_FOUR_HOURS) /
        SIXTY_MINUTES
    );
    const minutes = Math.round(
      (diffInSeconds -
        weeks * SEVEN_DAYS -
        days * TWENTY_FOUR_HOURS -
        hours * SIXTY_MINUTES) /
        ONE_MINUTE
    );
    return { months: 0, weeks, days, hours, minutes };
  }
  // Months
  const months = Math.floor(diffInSeconds / ONE_MONTH);
  const weeks = Math.floor((diffInSeconds - months * ONE_MONTH) / SEVEN_DAYS);
  const days = Math.floor(
    (diffInSeconds - months * ONE_MONTH - weeks * SEVEN_DAYS) /
      TWENTY_FOUR_HOURS
  );
  const hours = Math.floor(
    (diffInSeconds -
      months * ONE_MONTH -
      weeks * SEVEN_DAYS -
      days * TWENTY_FOUR_HOURS) /
      SIXTY_MINUTES
  );
  const minutes = Math.round(
    (diffInSeconds -
      months * ONE_MONTH -
      weeks * SEVEN_DAYS -
      days * TWENTY_FOUR_HOURS -
      hours * SIXTY_MINUTES) /
      ONE_MINUTE
  );
  return { months, weeks, days, hours, minutes };
};



// WEBPACK FOOTER //
// ./src/common/lib/formatReceiveRDAT.js